import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  MapPin, 
  Wrench, 
  FileText,
  Bell,
  User,
  X,
  Flame,
  Building2,
  Truck,
  ClipboardList,
  Archive
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { isAdmin } = useAuth();

  const adminMenuItems = [
    { name: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Clients', icon: Users, path: '/clients' },
    { name: 'Sites', icon: Building2, path: '/admin/sites' },
    { name: 'Plans', icon: MapPin, path: '/plans' },
    { name: 'Maintenances', icon: Wrench, path: '/maintenance' },
    { name: 'Rapports', icon: FileText, path: '/reports' },
    { name: 'Emplacement Armoires', icon: Archive, path: '/admin/cabinet-placements' },
    { name: 'Équipements Véhicules', icon: Truck, path: '/admin/vehicle-equipment' },
    { name: 'État Extincteurs', icon: ClipboardList, path: '/admin/extinguisher-inventory' },
    { name: 'Notifications', icon: Bell, path: '/notifications' },
    { name: 'Mon Profil', icon: User, path: '/admin-profile' },
  ];

  const clientMenuItems = [
    { name: 'Tableau de bord', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Mes Sites', icon: Building2, path: '/my-sites' },
    { name: 'Mon Plan', icon: MapPin, path: '/my-plan' },
    { name: 'Mes Rapports', icon: FileText, path: '/my-reports' },
    { name: 'Notifications', icon: Bell, path: '/notifications' },
    { name: 'Emplacement Armoires', icon: Archive, path: '/technical/cabinet-placements' },
    { name: 'Équipements Véhicules', icon: Truck, path: '/technical/vehicle-equipment' },
    { name: 'État Extincteurs', icon: ClipboardList, path: '/technical/extinguisher-inventory' },
    { name: 'Mon Profil', icon: User, path: '/profile' },
  ];

  const menuItems = isAdmin ? adminMenuItems : clientMenuItems;

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ease-in-out shadow-sm ${
          sidebarOpen 
            ? 'w-64 translate-x-0' 
            : 'w-64 -translate-x-full lg:translate-x-0 lg:w-20'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center border-b border-gray-200 transition-all ${
            sidebarOpen ? 'justify-between px-6 py-5' : 'justify-center px-2 py-4 lg:px-0'
          }`}>
            <motion.div
              className={`flex items-center ${sidebarOpen ? 'space-x-4' : 'justify-center'}`}
              whileHover={{ scale: 1.02 }}
            >
              <div className={`flex items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/30 ${
                sidebarOpen ? 'w-16 h-16' : 'w-12 h-12'
              }`}>
                <Flame className={`text-white ${sidebarOpen ? 'w-9 h-9' : 'w-7 h-7'}`} />
              </div>
              {sidebarOpen && (
                <div>
                  <h1 className="text-xl font-bold text-gray-900 tracking-tight">PFE</h1>
                  <p className="text-xs text-gray-500 font-medium">Gestion Incendie</p>
                </div>
              )}
            </motion.div>
            {sidebarOpen && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-all"
              >
                <X className="w-5 h-5" />
              </motion.button>
            )}
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-1.5 overflow-y-auto ${sidebarOpen ? 'px-3' : 'px-2'}`}>
            {menuItems.map((item, index) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => {
                  // Close sidebar on mobile when link is clicked
                  if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
                className={({ isActive }) =>
                  `group relative flex items-center rounded-lg transition-all duration-200 ${
                    sidebarOpen ? 'space-x-3 px-3 py-2.5' : 'justify-center px-3 py-2.5'
                  } ${
                    isActive
                      ? 'bg-red-50 text-red-600 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100 font-medium'
                  }`
                }
                title={!sidebarOpen ? item.name : ''}
              >
                {({ isActive }) => (
                  <motion.div
                    className={`flex items-center w-full ${sidebarOpen ? 'space-x-3' : 'justify-center'}`}
                    whileHover={sidebarOpen ? { x: 2 } : {}}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={isActive ? 2.5 : 2} />
                    {sidebarOpen && <span className="text-sm">{item.name}</span>}
                  </motion.div>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer */}
          {sidebarOpen && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500 text-center font-medium">
                © 2025 PETROFEU ÉQUIPEMENTS
              </p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;

